import { Checkbox, IconButton, Loader } from "@/atoms";
import {
  type Side,
  Props as TooltipProps,
} from "@/atoms/tooltips/Tooltip/Tooltip";
import {
  ComponentProps,
  PropsWithChildren,
  cloneElement,
  useEffect,
  useState,
} from "react";
import { useMedia } from "react-use";

import { Combobox } from "@headlessui/react";
import * as Popover from "@radix-ui/react-popover";
import { type VariantProps, cva } from "class-variance-authority";
import { AnimatePresence, motion } from "framer-motion";
import { Check, Search, X } from "lucide-react";
import { twMerge } from "tailwind-merge";

const select = cva(
  "pointer-events-auto z-[100] min-w-[242px] max-w-[320px] overflow-hidden rounded-md border bg-white shadow-md",
);

type SelectVariantProps = VariantProps<typeof select>;

export interface SelectOption<T> {
  id: string;
  label: string;
  sublabel?: string;
  startDecorator?: React.ReactElement<{
    className?: string;
  }>;
  endDecorator?: React.ReactNode;
  labelDecorator?: React.ReactElement<{
    className?: string;
  }>;
  selectionIndicator?: React.ReactElement<{
    className?: string;
  }>;
  isDisabled?: boolean;
  tooltip?: React.ReactElement<Pick<TooltipProps, "side">>;
  value: T;
}

type Alignment = "start" | "center" | "end";

export interface BaseProps<T> extends SelectVariantProps {
  options: SelectOption<T>[];
  isOpen?: boolean;
  /**
   * Whether to enable clearing the selection for single selects
   */
  enableClearingSelection?: boolean;
  /**
   * Whether to enable the searchbar
   */
  enableSearch?: boolean;
  /**
   * Placeholder text for the searchbar
   */
  searchPlaceholder?: string;
  /**
   * Optional header to display above the options
   */
  header?: React.ReactNode;
  side?: ComponentProps<typeof Popover.Content>["side"];
  /**
   * Where to align the select relative to the trigger
   */
  align?: Alignment;
  sideOffset?: number;
  alignOffset?: number;
  /**
   * Shows a loading indicator when true
   */
  container?: HTMLElement;
  isLoading?: boolean;
  /**
   * Whether to close the select when an option is selected. Defaults to true for single-select, and false for multiselect
   */
  closeOnSelect?: boolean;
  className?: string;
  style?: React.CSSProperties;
  onInputKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
}

export interface SingleSelectProps<T> extends BaseProps<T> {
  multiselect?: false | undefined;
  /**
   * Makes the select controlled. If not provided, it will be uncontrolled by default
   */
  value?: SelectOption<T> | null;
  /**
   * Default value for uncontrolled selects
   */
  defaultValue?: SelectOption<T> | null;
  onSelect?: (option: SelectOption<T> | null) => void;
}

export interface MultiSelectProps<T> extends BaseProps<T> {
  multiselect: true;
  value?: SelectOption<T>[];
  defaultValue?: SelectOption<T>[];
  onSelect?: (option: SelectOption<T>[]) => void;
}

export type ReusableProps<T> = SingleSelectProps<T> | MultiSelectProps<T>;

export type Props<T> = ReusableProps<T> &
  PropsWithChildren<{
    classNames?: {
      root?: string;
      option?: string;
      search?: {
        root?: string;
        input?: string;
        xButton?: string;
      };
    };
  }>;

/**
 * A popup with a list of options to pick from
 */
function Select<T>({
  options,
  value,
  defaultValue,
  multiselect = false,
  isOpen: controlledIsOpen,
  enableSearch = true,
  searchPlaceholder = "Search options...",
  header,
  side = "bottom",
  align = "end",
  sideOffset = 4,
  alignOffset,
  container,
  isLoading = false,
  closeOnSelect,
  children,
  className,
  classNames,
  style,
  onSelect,
  onInputKeyDown,
}: Props<T>) {
  const isMobile = useMedia("(max-width: 640px)");
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");

  let initialSelectedOption: SelectOption<T> | null = null;
  if (value === undefined && defaultValue !== undefined) {
    initialSelectedOption = Array.isArray(defaultValue) ? null : defaultValue;
  }

  let initialSelectedOptions: SelectOption<T>[] = [];
  if (value === undefined && defaultValue !== undefined) {
    initialSelectedOptions = Array.isArray(defaultValue) ? defaultValue : [];
  }

  // selectedOption and selectedOptions state are not used if value is provided (select is controlled)
  const [selectedOption, setSelectedOption] = useState<SelectOption<T> | null>(
    initialSelectedOption,
  );
  const [selectedOptions, setSelectedOptions] = useState<SelectOption<T>[]>(
    initialSelectedOptions,
  );

  useEffect(() => {
    if (!isOpen) {
      setSearch("");
    }
  }, [isOpen]);

  const shouldCloseOnSelect = closeOnSelect ?? (multiselect ? false : true);

  const filteredOptions = search
    ? options.filter((option) =>
        option.label.toLowerCase().includes(search.toLowerCase()),
      )
    : options;

  let defaultTooltipSide: Side = isMobile ? "bottom" : "left";
  if (!isMobile) {
    switch (align) {
      case "end":
        defaultTooltipSide = "left";
        break;
      case "start":
        defaultTooltipSide = "right";
        break;
      case "center":
        defaultTooltipSide = "left";
        break;
    }
  }

  const renderComboboxChildren = () => {
    return (
      <div>
        {header && header}
        {enableSearch && (
          <div
            className={twMerge(
              "flex items-center gap-1 border-b pl-3 pr-1.5",
              classNames?.search?.root,
            )}
          >
            <Search className="mr-2 h-5 w-5 shrink-0 text-gray-400" />
            <Combobox.Input
              value={search}
              placeholder={searchPlaceholder}
              className={twMerge(
                "flex h-10 w-full border-transparent bg-transparent px-0 py-3 text-sm outline-none placeholder:text-gray-400 focus:border-transparent focus:ring-0 disabled:cursor-not-allowed disabled:opacity-50",
                classNames?.search?.input,
              )}
              onKeyDown={onInputKeyDown}
              onChange={(e) => setSearch(e.target.value)}
              ref={(input: HTMLInputElement | null) => {
                // For some reason the input is not being focused on first render, so we force it to focus here
                // We use a callback ref instead of useRef/useEffect as something causes the input ref to be null on first render when created with useRef -- probably related to the intial focus issue in the first place
                if (input) {
                  input.focus();
                }
              }}
            />
            {search && (
              <IconButton
                icon={<X />}
                size="small"
                onClick={() => setSearch("")}
                className={classNames?.search?.xButton}
              />
            )}
          </div>
        )}
        <Combobox.Options className="max-h-[300px] overflow-y-auto" static>
          {(options.length === 0 || filteredOptions.length === 0) && (
            <p className="px-3 py-3 text-sm text-gray-500">
              {options.length === 0
                ? "No options available"
                : "No results found"}
            </p>
          )}
          {filteredOptions.map((option) => (
            <OptionalTooltip
              option={option}
              defaultSide={defaultTooltipSide}
              key={option.id}
            >
              {/* We wrap Combobox.Option in a div here to enable displaying tooltips properly when the option is disabled, as otherwise the disabled state prevents the tooltip from showing on hover */}
              <div>
                <Combobox.Option
                  value={option}
                  disabled={option.isDisabled}
                  className={twMerge(
                    "ui-active:bg-gray-100 flex cursor-pointer select-none items-center justify-between gap-3 px-3 py-3 text-sm text-black",
                    classNames?.option,
                    option.isDisabled ? "cursor-default opacity-25" : "",
                  )}
                >
                  <div className="flex min-w-0 items-center">
                    {option.startDecorator &&
                      cloneElement(option.startDecorator, {
                        className: twMerge(
                          "shrink-0",
                          option.startDecorator.props.className,
                        ),
                      })}
                    <div
                      className={twMerge(
                        "flex min-w-0 flex-col",
                        option.startDecorator ? "ml-3" : "",
                      )}
                    >
                      <div className="flex flex-wrap gap-2">
                        <span className="truncate">{option.label}</span>
                        {option.labelDecorator && option.labelDecorator}
                      </div>
                      {option.sublabel && (
                        <span className="truncate text-gray-500">
                          {option.sublabel}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    {option.endDecorator && option.endDecorator}
                    {option.selectionIndicator ? (
                      cloneElement(option.selectionIndicator, {
                        className: twMerge(
                          "ui-selected:visible invisible shrink-0",
                          option.selectionIndicator.props.className,
                        ),
                      })
                    ) : (
                      <>
                        {multiselect ? (
                          <>
                            <span className="ui-selected:block hidden">
                              <Checkbox checked={true} />
                            </span>
                            <span className="ui-selected:hidden block">
                              <Checkbox checked={false} />
                            </span>
                          </>
                        ) : (
                          <Check className="text-brand-500 ui-selected:visible invisible h-5 w-5 shrink-0" />
                        )}
                      </>
                    )}
                  </div>
                </Combobox.Option>
              </div>
            </OptionalTooltip>
          ))}
        </Combobox.Options>
      </div>
    );
  };

  return (
    <Popover.Root
      open={controlledIsOpen !== undefined ? controlledIsOpen : isOpen}
      onOpenChange={(_isOpen) => setIsOpen(_isOpen)}
    >
      <Popover.Trigger asChild>{children}</Popover.Trigger>
      <AnimatePresence>
        {(isOpen || controlledIsOpen) && (
          <Popover.Portal forceMount container={container}>
            <Popover.Content
              side={side}
              align={align}
              sideOffset={sideOffset}
              alignOffset={alignOffset}
              asChild
            >
              <motion.div
                initial="visible"
                exit="leave"
                variants={{
                  visible: {
                    opacity: 1,
                  },
                  leave: {
                    opacity: 0,
                    transition: {
                      ease: "easeOut",
                      duration: 0.075,
                    },
                  },
                }}
                className={twMerge(select(), className, classNames?.root)}
                style={style}
              >
                {isLoading ? (
                  <div className="p-3">
                    <Loader delayed />
                  </div>
                ) : (
                  <>
                    {/* Combobox's type definitions don't accept a boolean true | false for the multiple prop, only true or false directly, with the only current workaround being to duplicate the combobox */}
                    {multiselect ? (
                      <Combobox
                        multiple
                        by="id"
                        value={
                          value !== undefined && Array.isArray(value)
                            ? value
                            : selectedOptions
                        }
                        onChange={(newValue) => {
                          if (value === undefined) {
                            setSelectedOptions(newValue);
                          }

                          if (shouldCloseOnSelect) {
                            setIsOpen(false);
                          }

                          (onSelect as (option: SelectOption<T>[]) => void)?.(
                            newValue,
                          );
                        }}
                      >
                        {renderComboboxChildren()}
                      </Combobox>
                    ) : (
                      <Combobox
                        by="id"
                        value={
                          value !== undefined && !Array.isArray(value)
                            ? value
                            : selectedOption
                        }
                        onChange={(newValue) => {
                          if (value === undefined) {
                            setSelectedOption(newValue);
                          }

                          if (shouldCloseOnSelect) {
                            setIsOpen(false);
                          }

                          (
                            onSelect as (option: SelectOption<T> | null) => void
                          )?.(newValue);
                        }}
                      >
                        {renderComboboxChildren()}
                      </Combobox>
                    )}
                  </>
                )}
              </motion.div>
            </Popover.Content>
          </Popover.Portal>
        )}
      </AnimatePresence>
    </Popover.Root>
  );
}

export default Select;

function OptionalTooltip<T>({
  option,
  defaultSide,
  children,
}: {
  option: SelectOption<T>;
  defaultSide: Side;
  children: React.ReactNode;
}) {
  if (!option.tooltip) {
    return children;
  }

  return (
    <>
      {cloneElement(
        option.tooltip,
        { side: option.tooltip.props.side ?? defaultSide },
        children,
      )}
    </>
  );
}
